import React, { useEffect, useRef, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Extension, mergeAttributes } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faItalic,
  faUnderline,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
  faListUl,
  faListOl,
  faLink,
  faUnlink,
  faTable,
  faTrash,
  faImage,
  faLayerGroup,
  faArrowUp,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { TEMPLATE_FONT_SIZE_OPTIONS } from '../../utils/templateEditorFontSize';

const IMAGE_NUDGE_STEP_PX = 10;
// Each click scales both dimensions by this fraction at once (10%), off of whatever the image's
// current width is (its saved width attribute, or its natural size the first time it's resized).
const IMAGE_RESIZE_STEP = 0.1;
const IMAGE_MIN_WIDTH_PX = 20;
const IMAGE_MAX_WIDTH_PX = 2000;

// Matches .contract-editor-content h1/h2/h3 in modals.css. Baked in as an explicit per-run
// fontSize mark (below) when a heading is turned on, rather than left to that CSS class alone -
// the class only exists for the live editor, so relying on it would mean the saved HTML has no
// size information of its own for wherever it's rendered afterward (the print pipeline).
const HEADING_FONT_SIZES = { 1: 24, 2: 20, 3: 16 };

// TipTap's TextAlign extension only affects inline content within a block (paragraphs/headings) -
// it has no effect on the image node itself, which renders as its own block. Two modes, both via
// inline style so they round-trip through saved HTML and the print pipeline with no separate
// handling needed there:
//  - normal (default): plain block image, margin auto-tricks for left/center/right alignment.
//  - overlay: the node renders as a zero-height wrapper div containing an absolutely positioned
//    image - the wrapper reserves no flow space, so text lays out as if the image weren't there,
//    and the image sits visually on top of it (not part of the text formatting at all). Freely
//    repositioned via offsetX/offsetY (nudged by toolbar buttons), applied as a plain transform -
//    no drag/NodeView needed, so this stays a simple attribute update like align does.
const ALIGN_STYLES = {
  left: 'display: block; margin: 0 auto 0 0;',
  center: 'display: block; margin: 0 auto;',
  right: 'display: block; margin: 0 0 0 auto;'
};
export const AlignableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent(),
      align: {
        default: 'left',
        parseHTML: (element) => element.getAttribute('data-align') || 'left',
        renderHTML: (attributes) => ({ 'data-align': attributes.align || 'left' })
      },
      overlay: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-overlay') === 'true',
        renderHTML: (attributes) => ({ 'data-overlay': attributes.overlay ? 'true' : 'false' })
      },
      offsetX: {
        default: 0,
        parseHTML: (element) => parseInt(element.getAttribute('data-offset-x'), 10) || 0,
        renderHTML: (attributes) => ({ 'data-offset-x': String(attributes.offsetX || 0) })
      },
      offsetY: {
        default: 0,
        parseHTML: (element) => parseInt(element.getAttribute('data-offset-y'), 10) || 0,
        renderHTML: (attributes) => ({ 'data-offset-y': String(attributes.offsetY || 0) })
      },
      // Explicit pixel width (height omitted - "auto" keeps the aspect ratio, so both dimensions
      // still scale together from a single number). null/unset means natural size, unchanged
      // from before this existed - this attribute's own renderHTML contributes just the width
      // half of the style string; the node's renderHTML below (which sets the rest: alignment
      // margins, or the overlay's position/transform) merges cleanly with it since mergeAttributes
      // concatenates multiple "style" values instead of one clobbering the other.
      width: {
        default: null,
        parseHTML: (element) => {
          const px = parseInt(element.style.width, 10);
          return Number.isFinite(px) ? px : null;
        },
        renderHTML: (attributes) => attributes.width ? { style: `width: ${attributes.width}px; height: auto;` } : {}
      }
    };
  },
  renderHTML({ HTMLAttributes, node }) {
    if (node.attrs.overlay) {
      const x = node.attrs.offsetX || 0;
      const y = node.attrs.offsetY || 0;
      return ['div', { style: 'position: relative; height: 0; overflow: visible;' },
        ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          style: `position: absolute; top: 0; left: 0; z-index: 5; max-width: 60%; transform: translate(${x}px, ${y}px);`
        })]
      ];
    }
    const align = ALIGN_STYLES[node.attrs.align] ? node.attrs.align : 'left';
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { style: ALIGN_STYLES[align] })];
  },
  // Same nudge the toolbar's arrow buttons do, but from the keyboard once the image is selected
  // (clicking it creates a NodeSelection) and in overlay mode. Returning false for every other
  // case matters - it lets the default arrow-key behavior (moving the selection elsewhere) run
  // as normal instead of swallowing the keypress.
  addKeyboardShortcuts() {
    const nudge = (dx, dy) => () => {
      const { selection } = this.editor.state;
      if (!(selection instanceof NodeSelection) || selection.node.type.name !== 'image' || !selection.node.attrs.overlay) {
        return false;
      }
      const attrs = selection.node.attrs;
      this.editor.chain().focus().updateAttributes('image', {
        offsetX: (attrs.offsetX || 0) + dx,
        offsetY: (attrs.offsetY || 0) + dy,
      }).run();
      return true;
    };
    return {
      ArrowUp: nudge(0, -IMAGE_NUDGE_STEP_PX),
      ArrowDown: nudge(0, IMAGE_NUDGE_STEP_PX),
      ArrowLeft: nudge(-IMAGE_NUDGE_STEP_PX, 0),
      ArrowRight: nudge(IMAGE_NUDGE_STEP_PX, 0),
    };
  }
});

// A per-selection font-size mark, on top of TextStyle - the same mechanism Color uses to attach
// its own attribute to that mark. This is deliberately separate from the toolbar's font-size
// picker's other job (setting the whole document's base/default size, saved via
// wrapTemplateContent): that stays a single value for the page; this lets an actual selection
// (or the text about to be typed at the cursor, same as toggling Bold with nothing selected)
// override it locally, exactly like bold/italic/color already do.
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (element) => element.style.fontSize || null,
          renderHTML: (attributes) => {
            if (!attributes.fontSize) return {};
            return { style: `font-size: ${attributes.fontSize}` };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// The one shared TipTap extension set used by every template editor (PTO decision templates,
// contracts, declarations) - deliberately not configurable per caller, see TemplateEditorWorkspace.
export const TEMPLATE_EDITOR_EXTENSIONS = [
  StarterKit,
  TextStyle,
  Color,
  FontSize,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
    defaultAlignment: 'left',
  }),
  Underline,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: { class: 'contract-link' },
  }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  AlignableImage,
];

// The single shared TipTap toolbar + editor surface used by every template editor. It doesn't
// take feature flags: every caller gets the exact same toolbar (bold/italic/underline, headings,
// image-aware alignment, lists, link, image with overlay/nudge, table, font size, color) - see
// the extraction plan for why differences between callers were deliberately not preserved.
//
// Props:
//  - editor: the Editor instance, created by the caller via useEditor({ extensions:
//    TEMPLATE_EDITOR_EXTENSIONS, shouldRerenderOnTransaction: true, ... }).
//  - fontSize / onFontSizeChange: controlled - the caller owns the state (and its own dirty-check
//    against an "original" value), this component just renders the picker and applies the size.
//  - isEditorEmpty: whether to show the custom placeholder text.
//  - onUploadImage: async (file) => url - caller-specific (each template type has its own upload
//    endpoint); errors it throws are reported via onUploadError.
//  - onUploadError: (message) => void, optional.
const TemplateEditorWorkspace = ({
  editor,
  fontSize,
  onFontSizeChange,
  isEditorEmpty,
  onUploadImage,
  onUploadError,
}) => {
  const { t } = useLanguage();
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageFileInputRef = useRef(null);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    try {
      const dom = editor.view.dom;
      dom.style.fontSize = `${fontSize}px`;
      dom.setAttribute('data-unified-font-size', 'true');
    } catch {
      // View not mounted yet
    }
  }, [editor, fontSize]);

  // editor.isActive('image')/getAttributes('image') turned out unreliable for a NodeSelection
  // (the whole-image selection you get by clicking one) - reading the selection directly is
  // unambiguous, so every image-attribute read in this file goes through this instead.
  const getSelectedImageAttrs = () => {
    if (!editor) return null;
    const { selection } = editor.state;
    return selection instanceof NodeSelection && selection.node.type.name === 'image' ? selection.node.attrs : null;
  };

  // The actual rendered <img> DOM element for the selected image, so its natural (unscaled)
  // pixel size is available the first time it's resized (before any width attribute exists).
  const getSelectedImageDom = () => {
    if (!editor || !getSelectedImageAttrs()) return null;
    const dom = editor.view.nodeDOM(editor.state.selection.from);
    if (!dom) return null;
    return dom.tagName === 'IMG' ? dom : dom.querySelector('img');
  };

  // The current selection's (or, with the cursor collapsed, the "about to be typed") font-size
  // mark, if one is set - null falls back to the document's base size in the picker below.
  const getSelectionFontSize = () => {
    const raw = editor?.getAttributes('textStyle')?.fontSize;
    return raw ? parseInt(raw, 10) : null;
  };

  const handleFontSizeSelect = (e) => {
    const next = Number(e.target.value);
    if (!editor) return;
    const { selection } = editor.state;
    if (!selection.empty) {
      // Text is selected - resize just that run, same as bold/italic/color.
      editor.chain().focus().setFontSize(`${next}px`).run();
      return;
    }
    // Nothing selected - normally this sets the document's base/default size instead (below).
    // But headings render at their own fixed size in CSS regardless of that base (so it wouldn't
    // otherwise be reachable from a heading at all) - resize the whole heading's text directly,
    // as if it had been selected, instead of silently doing nothing visible.
    if (editor.isActive('heading')) {
      const { $from } = selection;
      editor.chain().focus().setTextSelection({ from: $from.start(), to: $from.end() }).setFontSize(`${next}px`).run();
      return;
    }
    onFontSizeChange(next);
  };

  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor.chain().focus().toggleUnderline().run();
  // toggleHeading only changes the block type - it doesn't touch marks, so a run resized earlier
  // (explicitly, or by the "cursor placed in a heading" case below) would otherwise keep
  // overriding the heading's size with a leftover inline style, while the heading's own
  // spacing/weight (being on the block element, not the text run) update correctly - looking
  // like only spacing changed. Baking HEADING_FONT_SIZES in explicitly (rather than just
  // clearing the mark and letting the editor's CSS class carry it) means the saved HTML carries
  // its own size wherever it's rendered next, print included. Turning the heading back off goes
  // the other way - clear the mark so the text returns to normal/paragraph sizing.
  const setHeading = (level) => {
    const turningOff = editor.isActive('heading', { level });
    const chain = editor.chain().focus().toggleHeading({ level });
    (turningOff ? chain.unsetFontSize() : chain.setFontSize(`${HEADING_FONT_SIZES[level]}px`)).run();
  };
  // Toggling a heading off (clicking the same H button again) already reverts to a paragraph,
  // but that's not obvious - this gives switching back to normal text its own explicit button.
  const setParagraph = () => editor.chain().focus().setParagraph().unsetFontSize().run();
  // Shared with image alignment: when an image is selected, the same left/center/right buttons
  // align the image instead (images don't support justify). Also turns overlay back off and
  // clears any nudge offset, so these buttons double as the way back to normal (regular) mode.
  const setTextAlign = (alignment) => {
    if (getSelectedImageAttrs()) {
      if (alignment === 'justify') return;
      editor.chain().focus().updateAttributes('image', { align: alignment, overlay: false, offsetX: 0, offsetY: 0 }).run();
      return;
    }
    editor.chain().focus().setTextAlign(alignment).run();
  };
  const toggleImageOverlay = () => {
    const isOverlay = getSelectedImageAttrs()?.overlay;
    // Reset the offset when leaving overlay, so re-enabling it later starts fresh at (0, 0)
    // instead of resurfacing wherever it was last nudged to.
    const attrs = isOverlay ? { overlay: false, offsetX: 0, offsetY: 0 } : { overlay: true };
    editor.chain().focus().updateAttributes('image', attrs).run();
  };
  const nudgeImage = (dx, dy) => {
    const attrs = getSelectedImageAttrs() || {};
    editor.chain().focus().updateAttributes('image', {
      offsetX: (attrs.offsetX || 0) + dx,
      offsetY: (attrs.offsetY || 0) + dy
    }).run();
  };
  // Scales both dimensions together by one step, off of the current width attribute if the
  // image was already resized before, otherwise its actual rendered (natural) size.
  const resizeImage = (grow) => {
    const attrs = getSelectedImageAttrs();
    if (!attrs) return;
    const currentWidth = attrs.width || getSelectedImageDom()?.naturalWidth;
    if (!currentWidth) return;
    const nextWidth = Math.round(currentWidth * (grow ? 1 + IMAGE_RESIZE_STEP : 1 - IMAGE_RESIZE_STEP));
    editor.chain().focus().updateAttributes('image', {
      width: Math.min(IMAGE_MAX_WIDTH_PX, Math.max(IMAGE_MIN_WIDTH_PX, nextWidth))
    }).run();
  };
  const setColor = (color) => editor.chain().focus().setColor(color).run();
  const toggleBulletList = () => editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () => editor.chain().focus().toggleOrderedList().run();

  const setLink = () => {
    const url = window.prompt(t('settings.enterUrl'));
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const unsetLink = () => editor.chain().focus().unsetLink().run();

  const insertLayoutTable = (cols) => {
    editor.chain().focus().insertTable({ rows: 1, cols, withHeaderRow: false }).run();
  };

  const addTableColumn = () => editor.chain().focus().addColumnAfter().run();
  const deleteTableColumn = () => editor.chain().focus().deleteColumn().run();
  const addTableRow = () => editor.chain().focus().addRowAfter().run();
  const deleteTableRow = () => editor.chain().focus().deleteRow().run();
  const deleteCurrentTable = () => editor.chain().focus().deleteTable().run();

  const handleImageButtonClick = () => {
    imageFileInputRef.current?.click();
  };

  const handleImageFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow selecting the same file again later
    if (!file) return;

    setUploadingImage(true);
    try {
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      console.error('Error uploading image:', err);
      onUploadError?.(err.response?.data?.error || t('settings.failedToUploadImage'));
    } finally {
      setUploadingImage(false);
    }
  };

  // Clicking an image already selects it (ProseMirror node selection), but with no visible
  // affordance for what to do next - this button gives delete a discoverable home, matching
  // the table toolbar's pattern of buttons that act on whatever node is currently active.
  const deleteSelectedImage = () => editor.chain().focus().deleteSelection().run();
  const isImageOverlayActive = () => !!getSelectedImageAttrs()?.overlay;

  const getCurrentAlignment = () => {
    if (!editor) return 'left';
    try {
      const imageAttrs = getSelectedImageAttrs();
      if (imageAttrs) {
        return imageAttrs.align || 'left';
      }
      if (editor.isActive({ textAlign: 'center' })) return 'center';
      if (editor.isActive({ textAlign: 'right' })) return 'right';
      if (editor.isActive({ textAlign: 'justify' })) return 'justify';
      if (editor.isActive({ textAlign: 'left' })) return 'left';
      const attrs = editor.getAttributes('textAlign');
      if (attrs?.textAlign) return attrs.textAlign;
      return 'left';
    } catch (e) {
      return 'left';
    }
  };

  const isAlignmentActive = (alignment) => getCurrentAlignment() === alignment;

  return (
    <div className="rich-text-editor">
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <button type="button" onClick={toggleBold} className={editor?.isActive('bold') ? 'active' : ''} title={t('settings.bold')}>
            <FontAwesomeIcon icon={faBold} />
          </button>
          <button type="button" onClick={toggleItalic} className={editor?.isActive('italic') ? 'active' : ''} title={t('settings.italic')}>
            <FontAwesomeIcon icon={faItalic} />
          </button>
          <button type="button" onClick={toggleUnderline} className={editor?.isActive('underline') ? 'active' : ''} title={t('settings.underline')}>
            <FontAwesomeIcon icon={faUnderline} />
          </button>
        </div>

        <div className="toolbar-group">
          <button type="button" onClick={setParagraph} className={editor?.isActive('paragraph') ? 'active' : ''} title={t('settings.paragraphText')}>P</button>
          <button type="button" onClick={() => setHeading(1)} className={editor?.isActive('heading', { level: 1 }) ? 'active' : ''} title={t('settings.heading1')}>H1</button>
          <button type="button" onClick={() => setHeading(2)} className={editor?.isActive('heading', { level: 2 }) ? 'active' : ''} title={t('settings.heading2')}>H2</button>
          <button type="button" onClick={() => setHeading(3)} className={editor?.isActive('heading', { level: 3 }) ? 'active' : ''} title={t('settings.heading3')}>H3</button>
        </div>

        <div className="toolbar-group">
          <button type="button" onClick={() => setTextAlign('left')} className={isAlignmentActive('left') ? 'active' : ''} title={t('settings.alignLeft')}>
            <FontAwesomeIcon icon={faAlignLeft} />
          </button>
          <button type="button" onClick={() => setTextAlign('center')} className={isAlignmentActive('center') ? 'active' : ''} title={t('settings.alignCenter')}>
            <FontAwesomeIcon icon={faAlignCenter} />
          </button>
          <button type="button" onClick={() => setTextAlign('right')} className={isAlignmentActive('right') ? 'active' : ''} title={t('settings.alignRight')}>
            <FontAwesomeIcon icon={faAlignRight} />
          </button>
          <button type="button" onClick={() => setTextAlign('justify')} disabled={!!getSelectedImageAttrs()} className={isAlignmentActive('justify') ? 'active' : ''} title={t('settings.justify')}>
            <FontAwesomeIcon icon={faAlignJustify} />
          </button>
        </div>

        <div className="toolbar-group">
          <button type="button" onClick={toggleBulletList} className={editor?.isActive('bulletList') ? 'active' : ''} title={t('settings.bulletList')}>
            <FontAwesomeIcon icon={faListUl} />
          </button>
          <button type="button" onClick={toggleOrderedList} className={editor?.isActive('orderedList') ? 'active' : ''} title={t('settings.numberedList')}>
            <FontAwesomeIcon icon={faListOl} />
          </button>
        </div>

        <div className="toolbar-group">
          <button type="button" onClick={setLink} className={editor?.isActive('link') ? 'active' : ''} title={t('settings.addLink')}>
            <FontAwesomeIcon icon={faLink} />
          </button>
          <button type="button" onClick={unsetLink} disabled={!editor?.isActive('link')} title={t('settings.removeLink')}>
            <FontAwesomeIcon icon={faUnlink} />
          </button>
        </div>

        <div className="toolbar-group">
          <button type="button" onClick={handleImageButtonClick} disabled={uploadingImage} title={t('settings.insertImage')}>
            <FontAwesomeIcon icon={faImage} />
          </button>
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileSelected}
            style={{ display: 'none' }}
          />
          <button type="button" onClick={deleteSelectedImage} disabled={!getSelectedImageAttrs()} title={t('settings.deleteImage')}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <button type="button" onClick={toggleImageOverlay} disabled={!getSelectedImageAttrs()} className={isImageOverlayActive() ? 'active' : ''} title={t('settings.overlayImage')}>
            <FontAwesomeIcon icon={faLayerGroup} />
          </button>
          <button type="button" onClick={() => nudgeImage(0, -IMAGE_NUDGE_STEP_PX)} disabled={!isImageOverlayActive()} title={t('settings.nudgeImageUp')}>
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
          <button type="button" onClick={() => nudgeImage(0, IMAGE_NUDGE_STEP_PX)} disabled={!isImageOverlayActive()} title={t('settings.nudgeImageDown')}>
            <FontAwesomeIcon icon={faArrowDown} />
          </button>
          <button type="button" onClick={() => nudgeImage(-IMAGE_NUDGE_STEP_PX, 0)} disabled={!isImageOverlayActive()} title={t('settings.nudgeImageLeft')}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button type="button" onClick={() => nudgeImage(IMAGE_NUDGE_STEP_PX, 0)} disabled={!isImageOverlayActive()} title={t('settings.nudgeImageRight')}>
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <button type="button" onClick={() => resizeImage(false)} disabled={!getSelectedImageAttrs()} title={t('settings.shrinkImage')}>
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
          </button>
          <button type="button" onClick={() => resizeImage(true)} disabled={!getSelectedImageAttrs()} title={t('settings.enlargeImage')}>
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </button>
        </div>

        <div className="toolbar-group">
          <button type="button" onClick={() => insertLayoutTable(2)} title={t('settings.insertTable2Col')}>
            <FontAwesomeIcon icon={faTable} /> 2
          </button>
          <button type="button" onClick={addTableColumn} disabled={!editor?.isActive('table')} title={t('settings.addTableColumn')}>+Col</button>
          <button type="button" onClick={deleteTableColumn} disabled={!editor?.isActive('table')} title={t('settings.deleteTableColumn')}>−Col</button>
          <button type="button" onClick={addTableRow} disabled={!editor?.isActive('table')} title={t('settings.addTableRow')}>+Row</button>
          <button type="button" onClick={deleteTableRow} disabled={!editor?.isActive('table')} title={t('settings.deleteTableRow')}>−Row</button>
          <button type="button" onClick={deleteCurrentTable} disabled={!editor?.isActive('table')} title={t('settings.deleteTable')}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>

        <div className="toolbar-group">
          <select
            value={getSelectionFontSize() || fontSize}
            onChange={handleFontSizeSelect}
            title={t('settings.templateFontSize', 'Font size')}
            className="editor-font-size-select"
          >
            {TEMPLATE_FONT_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}px</option>
            ))}
          </select>
        </div>

        <div className="toolbar-group">
          <input type="color" onChange={(e) => setColor(e.target.value)} title={t('settings.textColor')} className="color-picker" />
        </div>
      </div>

      <div className="editor-content">
        <EditorContent editor={editor} />
        {isEditorEmpty && (
          <div className="custom-placeholder">{t('settings.startWriting')}</div>
        )}
      </div>
    </div>
  );
};

export default TemplateEditorWorkspace;
